import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import Header from './Header';
import Seo from './Seo';
import Footer from './Footer';
import FaqSchema from './FaqSchema';
import ProductSidebar from './ProductSidebar';
import { useUser } from '../contexts/UserContext';
import CertificationPage from './CertificationPage';
import './CertificationLanding.css';

const HERO_IMG = '/images/certification/hero-academy.png';
const ROADMAP_IMG = '/images/certification/roadmap-blueprint.png';
const QUIZ_IMG = '/images/certification/quiz-mastery.png';
const CERT_IMG = '/images/certification/certificate-glow.png';

/* ---------- Three.js orbiting roadmap nodes canvas ---------- */
const RoadmapCanvas = () => {
  const mountRef = useRef(null);
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    let width = mount.clientWidth;
    let height = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.z = 75;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const sprite = (() => {
      const c = document.createElement('canvas');
      c.width = c.height = 64;
      const ctx = c.getContext('2d');
      const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(0.35, 'rgba(255,209,102,0.95)');
      g.addColorStop(0.7, 'rgba(147,112,219,0.4)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 64, 64);
      return new THREE.CanvasTexture(c);
    })();

    // 12 roadmap "checkpoint" particles on a 3D path
    const NODES = 12;
    const positions = new Float32Array(NODES * 3);
    const phases = new Float32Array(NODES);
    for (let i = 0; i < NODES; i++) {
      phases[i] = (i / NODES) * Math.PI * 2;
    }
    const nodesGeo = new THREE.BufferGeometry();
    nodesGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const nodesMat = new THREE.PointsMaterial({
      size: 4.0, map: sprite, transparent: true,
      depthWrite: false, blending: THREE.AdditiveBlending,
      color: 0xffffff,
    });
    const nodes = new THREE.Points(nodesGeo, nodesMat);
    scene.add(nodes);

    // ambient particle field
    const fieldCount = 200;
    const fieldPos = new Float32Array(fieldCount * 3);
    const fieldSpeeds = new Float32Array(fieldCount);
    for (let i = 0; i < fieldCount; i++) {
      fieldPos[i * 3] = (Math.random() - 0.5) * 180;
      fieldPos[i * 3 + 1] = (Math.random() - 0.5) * 110;
      fieldPos[i * 3 + 2] = (Math.random() - 0.5) * 60;
      fieldSpeeds[i] = 0.04 + Math.random() * 0.10;
    }
    const fieldGeo = new THREE.BufferGeometry();
    fieldGeo.setAttribute('position', new THREE.BufferAttribute(fieldPos, 3));
    const fieldMat = new THREE.PointsMaterial({
      size: 1.0, map: sprite, transparent: true,
      depthWrite: false, blending: THREE.AdditiveBlending,
      color: 0xffffff, opacity: 0.4,
    });
    const field = new THREE.Points(fieldGeo, fieldMat);
    scene.add(field);

    // central graduation orb
    const orbGeo = new THREE.IcosahedronGeometry(7, 1);
    const orb = new THREE.Mesh(orbGeo, new THREE.MeshBasicMaterial({
      color: 0xffd166, wireframe: true, transparent: true, opacity: 0.45,
    }));
    scene.add(orb);

    let raf;
    let mx = 0, my = 0, t = 0;
    const onMove = (e) => {
      const r = mount.getBoundingClientRect();
      mx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      my = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);
    const onResize = () => {
      width = mount.clientWidth; height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', onResize);

    const animate = () => {
      t += 0.01;
      // orbital path with figure-8 arc
      const np = nodesGeo.attributes.position.array;
      for (let i = 0; i < NODES; i++) {
        const a = phases[i] + t * 0.5;
        np[i * 3]     = Math.cos(a) * 30;
        np[i * 3 + 1] = Math.sin(a * 2) * 12;
        np[i * 3 + 2] = Math.sin(a) * 14;
      }
      nodesGeo.attributes.position.needsUpdate = true;

      const fp = fieldGeo.attributes.position.array;
      for (let i = 0; i < fieldCount; i++) {
        fp[i * 3 + 1] += fieldSpeeds[i] * 0.5;
        if (fp[i * 3 + 1] > 55) fp[i * 3 + 1] = -55;
      }
      fieldGeo.attributes.position.needsUpdate = true;

      orb.rotation.x += 0.005;
      orb.rotation.y += 0.007;

      camera.position.x += (mx * 6 - camera.position.x) * 0.04;
      camera.position.y += (-my * 4 - camera.position.y) * 0.04;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('resize', onResize);
      nodesGeo.dispose(); nodesMat.dispose();
      fieldGeo.dispose(); fieldMat.dispose();
      orbGeo.dispose(); orb.material.dispose();
      sprite.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="ccl-hero-canvas" aria-hidden="true" />;
};

/* ---------- Roadmap ladder demo (modules light up sequentially) ---------- */
const ROADMAP_STEPS = [
  { week: 1, title: 'SQL fundamentals', day: 'Day 1–3' },
  { week: 1, title: 'Joins & subqueries', day: 'Day 4–5' },
  { week: 1, title: 'Window functions', day: 'Day 6–7' },
  { week: 2, title: 'Indexing strategy', day: 'Day 8–9' },
  { week: 2, title: 'Query optimization', day: 'Day 10–11' },
  { week: 2, title: 'Capstone: build a reporting query', day: 'Day 12–14' },
];

const RoadmapDemo = () => {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting && revealed === 0) setRevealed(1); }),
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [revealed]);
  useEffect(() => {
    if (revealed === 0 || revealed >= ROADMAP_STEPS.length) return;
    const t = setTimeout(() => setRevealed((r) => r + 1), 500);
    return () => clearTimeout(t);
  }, [revealed]);

  return (
    <section className="ccl-section" ref={ref}>
      <div className="ccl-section-grid">
        <div className="ccl-section-text">
          <span className="ccl-eyebrow">PERSONALIZED ROADMAP</span>
          <h2 className="ccl-h2">
            A day-by-day plan, <span className="ccl-grad-text">tailored to your goal</span>.
          </h2>
          <p className="ccl-lead">
            Tell us what you want to learn, how many hours per day, and how long. The AI builds
            a complete week-by-week, day-by-day curriculum — so you always know exactly what
            you're learning today and what's next.
          </p>
          <ul className="ccl-bullet-list">
            <li><span className="ccl-dot" />Calibrated to your daily time budget</li>
            <li><span className="ccl-dot" />Builds from fundamentals → advanced</li>
            <li><span className="ccl-dot" />Capstone project at the end of every track</li>
          </ul>
        </div>

        <div className="ccl-roadmap-card">
          <div className="ccl-roadmap-head">
            <span className="ccl-roadmap-pulse" />
            <span>Sample · "Master SQL in 14 days · 1h/day"</span>
          </div>
          <div className="ccl-roadmap-list">
            {ROADMAP_STEPS.map((step, i) => (
              <div
                key={i}
                className={`ccl-roadmap-row ${i < revealed ? 'lit' : ''}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="ccl-roadmap-week">W{step.week}</div>
                <div className="ccl-roadmap-meat">
                  <div className="ccl-roadmap-title">{step.title}</div>
                  <div className="ccl-roadmap-day">{step.day}</div>
                </div>
                <div className="ccl-roadmap-check">{i < revealed - 1 ? '✓' : (i === revealed - 1 ? '●' : '○')}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Quiz demo (3 options, correct lights up) ---------- */
const QuizDemo = () => {
  const ref = useRef(null);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) {
        setTimeout(() => setPicked('b'), 1200);
      } }),
      { threshold: 0.45 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (picked === 'b') {
      const t = setInterval(() => setScore((s) => (s >= 87 ? s : s + 3)), 30);
      return () => clearInterval(t);
    }
  }, [picked]);

  const circumference = 2 * Math.PI * 32;
  const offset = circumference * (1 - score / 100);

  return (
    <section className="ccl-section" ref={ref}>
      <div className="ccl-section-grid ccl-flip">
        <div className="ccl-quiz-card">
          <div className="ccl-quiz-meta">
            <span>MODULE 4 · END-OF-DAY QUIZ</span>
            <div className="ccl-quiz-ring">
              <svg viewBox="0 0 80 80">
                <defs>
                  <linearGradient id="cclQuizGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#9370db" />
                    <stop offset="100%" stopColor="#ffd166" />
                  </linearGradient>
                </defs>
                <circle cx="40" cy="40" r="32" stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
                <circle
                  cx="40" cy="40" r="32"
                  stroke="url(#cclQuizGrad)" strokeWidth="6" fill="none" strokeLinecap="round"
                  strokeDasharray={circumference} strokeDashoffset={offset}
                  transform="rotate(-90 40 40)"
                  style={{ transition: 'stroke-dashoffset 0.05s linear' }}
                />
              </svg>
              <span className="ccl-quiz-ring-num">{score}</span>
            </div>
          </div>
          <p className="ccl-quiz-q">"Which join returns rows with no match in either table?"</p>
          <div className="ccl-quiz-options">
            <div className={`ccl-quiz-opt ${picked === 'a' ? 'wrong' : ''}`}>
              <span className="ccl-quiz-letter">A</span>
              <span>INNER JOIN</span>
            </div>
            <div className={`ccl-quiz-opt ${picked === 'b' ? 'right' : ''}`}>
              <span className="ccl-quiz-letter">B</span>
              <span>FULL OUTER JOIN</span>
            </div>
            <div className={`ccl-quiz-opt ${picked === 'c' ? 'wrong' : ''}`}>
              <span className="ccl-quiz-letter">C</span>
              <span>LEFT JOIN</span>
            </div>
          </div>
        </div>

        <div className="ccl-section-text">
          <span className="ccl-eyebrow">MASTERY-BASED PROGRESSION</span>
          <h2 className="ccl-h2">
            Don't just <span className="ccl-grad-text">finish modules</span> — master them.
          </h2>
          <p className="ccl-lead">
            Every module ends with a short quiz that tests genuine understanding. Score 70%+ to
            unlock the next module — so you actually retain what you've learned, not just
            speed-run through it.
          </p>
          <ul className="ccl-bullet-list">
            <li><span className="ccl-dot" />70% pass threshold per module</li>
            <li><span className="ccl-dot" />Re-take if you miss</li>
            <li><span className="ccl-dot" />Score history saved per module</li>
          </ul>
        </div>
      </div>
    </section>
  );
};

/* ---------- Process flow ---------- */
const FLOW_STEPS = [
  { title: 'Pick your goal', body: 'What do you want to learn? SQL, React, ML, AWS — anything technical or career-focused.' },
  { title: 'Set your pace', body: 'How many hours per day, how many days. We size the roadmap to your real schedule.' },
  { title: 'AI builds the curriculum', body: 'Day-by-day modules across weeks, building from fundamentals to capstone in ~30 seconds.' },
  { title: 'Learn one module a day', body: 'Each module: lesson + interactive examples + 5-question quiz at the end.' },
  { title: 'Pass to progress', body: '70% on the quiz unlocks the next module. No skipping. Real mastery.' },
  { title: 'Earn the certificate', body: 'Complete the capstone, get your verified completion certificate to share on LinkedIn.' },
];

const FlowSection = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(-1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(0); }),
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (active < 0 || active >= FLOW_STEPS.length - 1) return;
    const t = setTimeout(() => setActive((a) => a + 1), 700);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <section className="ccl-section" ref={ref}>
      <div className="ccl-section-head">
        <span className="ccl-eyebrow">HOW IT WORKS</span>
        <h2 className="ccl-h2">
          Six steps. <span className="ccl-grad-text">Real mastery.</span>
        </h2>
        <p className="ccl-lead ccl-center">
          From "I should learn X" to "I have a verified certificate in X" — on a schedule that fits your life.
        </p>
      </div>
      <div className="ccl-flow-grid">
        {FLOW_STEPS.map((s, i) => (
          <div key={s.title} className={`ccl-flow-node ${i <= active ? 'lit' : ''}`}>
            <div className="ccl-flow-num">{String(i + 1).padStart(2, '0')}</div>
            <h3>{s.title}</h3>
            <p>{s.body}</p>
            {i < FLOW_STEPS.length - 1 && <div className={`ccl-flow-link ${i < active ? 'lit' : ''}`} />}
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------- Stats ---------- */
const STATS = [
  { val: 30, suffix: 's', label: 'To generate full roadmap' },
  { val: 70, suffix: '%', label: 'Quiz pass threshold' },
  { val: 3, suffix: '', label: 'Concurrent courses max' },
  { val: 65, suffix: 'K+', label: 'Courses completed' },
];
const useCounter = (target, start) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    const isFloat = !Number.isInteger(target);
    const dur = 1400; const t0 = performance.now(); let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = target * eased;
      setVal(isFloat ? Math.round(v * 10) / 10 : Math.round(v));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, start]);
  return val;
};
const StatTile = ({ stat, start }) => {
  const v = useCounter(stat.val, start);
  return (
    <div className="ccl-stat">
      <div className="ccl-stat-num">{v}{stat.suffix}</div>
      <div className="ccl-stat-lbl">{stat.label}</div>
    </div>
  );
};
const StatsBand = () => {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setStart(true); }),
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <section className="ccl-stats-band" ref={ref}>
      <div className="ccl-stats-glow" />
      {STATS.map((s) => <StatTile key={s.label} stat={s} start={start} />)}
    </section>
  );
};

/* ---------- FAQ ---------- */
const FAQS = [
  { q: 'How does the AI build the roadmap?', a: 'You give it a topic, your daily time budget, and a duration. It plans week-by-week, day-by-day modules — fundamentals first, then progressively harder content, ending in a capstone project. Generation takes about 30 seconds.' },
  { q: 'Do I have to finish in the timeframe I set?', a: 'No. The schedule is a guide, not a deadline. Take your time. Your progress is saved per module. Come back tomorrow, next week, or in a month — your roadmap is waiting.' },
  { q: 'What\'s in each module?', a: 'A focused lesson explaining the concept, interactive examples, and a 5-question quiz at the end. You need 70% to unlock the next module.' },
  { q: 'How many courses can I run at once?', a: 'Up to 3 active courses at any time. We cap it because real mastery comes from focus, not from collecting half-finished tracks.' },
  { q: 'What does the certificate say?', a: 'It states the course title, your name, completion date, and a verification ID. You can share it on LinkedIn, embed it on your portfolio, or download as PDF.' },
  { q: 'What topics are supported?', a: 'Anything teachable in 1–60 hour tracks: programming languages, frameworks, cloud platforms, machine learning, data, design, product, soft skills. Just type what you want to learn.' },
];

const FaqAccordion = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="ccl-section">
      <div className="ccl-section-head">
        <span className="ccl-eyebrow">QUESTIONS, ANSWERED</span>
        <h2 className="ccl-h2">Everything you wanted to ask</h2>
      </div>
      <div className="ccl-faq-list">
        {FAQS.map((f, i) => (
          <button key={f.q} type="button" className={`ccl-faq-item ${open === i ? 'open' : ''}`} onClick={() => setOpen(open === i ? -1 : i)}>
            <div className="ccl-faq-q">
              <span>{f.q}</span>
              <span className="ccl-faq-chev">+</span>
            </div>
            <div className="ccl-faq-a"><p>{f.a}</p></div>
          </button>
        ))}
      </div>
    </section>
  );
};

/* ---------- Main page ---------- */
const CertificationLanding = () => {
  const navigate = useNavigate();
  const studioRef = useRef(null);
  const goStudio = useCallback(() => {
    if (studioRef.current) studioRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  const { user } = useUser();
  const setShowAuthModal = useCallback((show) => { if (show) navigate('/?openLogin=1'); }, [navigate]);

  const heroRef = useRef(null);
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty('--mx', x);
      el.style.setProperty('--my', y);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div className="ccl-page">
      <Seo
        title="Certification Exam Prep with AI"
        description="Prepare for professional certification exams with AI practice questions and guided study."
        path="/certification"
      />
      <Header setShowAuthModal={setShowAuthModal} />
      <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />

      <section className="ccl-hero" ref={heroRef}>
        <RoadmapCanvas />
        <div className="ccl-hero-bg" />
        <div className="ccl-hero-halo" aria-hidden="true" />
        <img src={HERO_IMG} alt="" className="ccl-hero-img" loading="eager" />

        <div className="ccl-hero-content">
          <span className="ccl-hero-pill">
            <span className="ccl-pill-dot" /> AI roadmaps · daily quizzes · verified certificate
          </span>
          <h1 className="ccl-h1">
            Become an expert in <span className="ccl-grad-text">anything</span>.
          </h1>
          <p className="ccl-hero-sub">
            Pick a topic. Tell us your daily time budget. The AI builds a personalized day-by-day
            roadmap with quizzes that gate progress — so you actually master the material, then
            walk away with a verified certificate to prove it.
          </p>

          <div className="ccl-hero-cta">
            <button className="ccl-btn ccl-btn-primary" onClick={goStudio}>
              <span>Create my course</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="ccl-btn ccl-btn-ghost" onClick={() => navigate('/InterviewQuestions')}>
              Practice interviews instead
            </button>
          </div>

          <div className="ccl-hero-checks">
            <span>✓ Day-by-day curriculum</span>
            <span>✓ Quiz-gated progression</span>
            <span>✓ Verified certificate</span>
          </div>
        </div>

        {/* Floating glass course card */}
        <div className="ccl-floater" aria-hidden="true">
          <div className="ccl-floater-card">
            <div className="ccl-floater-head">
              <span className="ccl-floater-dot" />
              <span>IN PROGRESS · DAY 9 / 14</span>
            </div>
            <div className="ccl-floater-title">Master SQL · 14 days</div>
            <div className="ccl-floater-bar"><div className="ccl-floater-bar-fill" /></div>
            <div className="ccl-floater-meta">
              <span>Modules 8/14</span>
              <span>Avg quiz 92%</span>
            </div>
          </div>
          <div className="ccl-floater-glow" />
        </div>
      </section>

      <section className="ccl-studio" ref={studioRef} id="studio">
        <div className="ccl-studio-head">
          <span className="ccl-eyebrow">COURSE STUDIO</span>
          <h2 className="ccl-h2">
            Design your course — <span className="ccl-grad-text">live, in this page</span>.
          </h2>
          <p className="ccl-lead ccl-center">
            Pick a topic, set your pace, generate the roadmap, and start learning. All of it happens here.
          </p>
        </div>
        <CertificationPage embedded />
      </section>

      <RoadmapDemo />
      <QuizDemo />

      <section className="ccl-section">
        <div className="ccl-section-grid">
          <div className="ccl-section-text">
            <span className="ccl-eyebrow">VERIFIED CERTIFICATE</span>
            <h2 className="ccl-h2">
              Walk away with <span className="ccl-grad-text">proof</span>, not just hours.
            </h2>
            <p className="ccl-lead">
              Complete the capstone, pass the final assessment, and get a verified completion
              certificate with a unique verification ID. Share it on LinkedIn, embed it on your
              portfolio, attach it to job applications.
            </p>
            <ul className="ccl-bullet-list">
              <li><span className="ccl-dot" />Unique verification ID per certificate</li>
              <li><span className="ccl-dot" />Public verification page</li>
              <li><span className="ccl-dot" />LinkedIn-shareable + PDF export</li>
            </ul>
          </div>
          <div className="ccl-img-frame">
            <img src={CERT_IMG} alt="Verified certificate" />
            <div className="ccl-img-frame-glow" />
          </div>
        </div>
      </section>

      <FlowSection />
      <StatsBand />

      <section className="ccl-section">
        <div className="ccl-section-grid ccl-flip">
          <div className="ccl-img-frame">
            <img src={ROADMAP_IMG} alt="Personalized roadmap" />
            <div className="ccl-img-frame-glow" />
          </div>
          <div className="ccl-section-text">
            <span className="ccl-eyebrow">STRUCTURED PROGRESSION</span>
            <h2 className="ccl-h2">
              No more "where do I start?" — <span className="ccl-grad-text">we plan it</span>.
            </h2>
            <p className="ccl-lead">
              Every roadmap builds from fundamentals to advanced topics in a logical sequence.
              Each day's lesson assumes you've completed yesterday's. End-of-week capstones tie
              the concepts together.
            </p>
          </div>
        </div>
      </section>

      <section className="ccl-section">
        <div className="ccl-section-grid">
          <div className="ccl-section-text">
            <span className="ccl-eyebrow">QUIZ-GATED MASTERY</span>
            <h2 className="ccl-h2">
              <span className="ccl-grad-text">Quizzes that count</span>, not busy work.
            </h2>
            <p className="ccl-lead">
              Each module ends in a 5-question quiz on the actual material you just learned. 70%
              to unlock the next module. Miss it? Re-take. The bar exists because retention does.
            </p>
          </div>
          <div className="ccl-img-frame">
            <img src={QUIZ_IMG} alt="Quiz mastery" />
            <div className="ccl-img-frame-glow" />
          </div>
        </div>
      </section>

      <FaqAccordion />

      <section className="ccl-cta-final">
        <div className="ccl-cta-card">
          <div className="ccl-cta-glow" />
          <h2 className="ccl-h2">
            Stop "I'll learn it someday." <span className="ccl-grad-text">Start today.</span>
          </h2>
          <p>30 seconds to a roadmap. 14–60 days to mastery. One certificate to show for it.</p>
          <div className="ccl-hero-cta center">
            <button className="ccl-btn ccl-btn-primary" onClick={goStudio}>
              <span>Create my course</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="ccl-btn ccl-btn-ghost" onClick={() => navigate('/InterviewQuestions')}>Practice interviews</button>
          </div>
        </div>
      </section>

      <FaqSchema faqs={FAQS} />
      <Footer />
    </div>
  );
};

export default CertificationLanding;
